import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../utils/supabase';
import { useAuth } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheContext';
import { useCachedData } from './useCachedData';
import { CACHE_KEYS, CACHE_DURATIONS } from '../constants/cacheKeys';
import { useToast } from '../context/ToastContext';

export interface RubricCriterion {
  id: string;
  category_id: string;
  text: string;
  description?: string;
  display_order: number;
  created_at: string;
}

export interface RubricCategory {
  id: string;
  rubric_id: string;
  name: string;
  description: string;
  weight: number;
  display_order: number;
  created_at: string;
  // For backward compatibility
  criteria: string[];
  // New normalized structure
  criteria_items?: RubricCriterion[];
}

export interface Rubric {
  id: string;
  user_id: string;
  focus_id: string;
  template_type: string;
  name: string;
  description?: string;
  categories: RubricCategory[];
  is_active: boolean;
  created_at: string;
  updated_at: string;
  json_migrated?: boolean;
}

export interface RubricTemplate {
  id: string;
  template_type: string;
  name: string;
  description?: string;
  categories: RubricCategory[];
  is_default: boolean;
}

export function useRubric(focusId?: string) {
  const { user } = useAuth();
  const { invalidateCache } = useDataCache();
  const { showToast } = useToast();
  const [isCreating, setIsCreating] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Memoize the fetch function to prevent infinite loops
  const fetchRubric = useCallback(async () => {
    if (!user || !focusId) return null;

    const { data: rubricData, error } = await supabase
      .from('internship_rubrics')
      .select('*')
      .eq('focus_id', focusId)
      .eq('is_active', true)
      .maybeSingle();

    if (error) {
      // Don't throw for common "no data" scenarios
      if (error.code === 'PGRST116' || // No rows returned
          error.code === '406' || // Table not accessible
          error.message?.includes('0 rows')) {
        return null;
      }
      console.error('Error fetching rubric:', error);
      throw error;
    }

    if (!rubricData) return null;

    // If rubric has been migrated, fetch normalized data
    if (rubricData.json_migrated) {
      const { data: categories, error: catError } = await supabase
        .from('rubric_categories')
        .select(`
          *,
          rubric_criteria (*)
        `)
        .eq('rubric_id', rubricData.id)
        .order('display_order');

      if (!catError && categories) {
        // Transform to maintain backward compatibility
        rubricData.categories = categories.map(cat => ({
          ...cat,
          criteria: cat.rubric_criteria
            ?.sort((a, b) => a.display_order - b.display_order)
            .map(c => c.text) || [],
          criteria_items: cat.rubric_criteria
            ?.sort((a, b) => a.display_order - b.display_order) || []
        }));
      } else if (catError) {
        console.error('Error fetching normalized categories:', catError);
      }
    }

    return rubricData;
  }, [user, focusId]);

  // Fetch active rubric for a focus
  const { 
    data: activeRubric, 
    loading: rubricLoading, 
    refresh: refetchRubric 
  } = useCachedData<Rubric | null>(
    fetchRubric,
    {
      cacheKey: `${CACHE_KEYS.INTERNSHIP_RUBRIC}_${focusId}`,
      maxAge: CACHE_DURATIONS.INTERNSHIP_RUBRIC,
      enabled: !!user && !!focusId,
    }
  );

  // Memoize templates fetch function
  const fetchTemplates = useCallback(async () => {
    if (!user) return [];

    const { data, error } = await supabase
      .from('rubric_templates')
      .select('*')
      .order('is_default', { ascending: false });

    if (error) {
      console.error('Error fetching templates:', error);
      return [];
    }

    return data || [];
  }, [user]);

  // Fetch available templates
  const { 
    data: templates, 
    loading: templatesLoading 
  } = useCachedData<RubricTemplate[]>(
    fetchTemplates,
    {
      cacheKey: CACHE_KEYS.RUBRIC_TEMPLATES,
      maxAge: CACHE_DURATIONS.RUBRIC_TEMPLATES,
      enabled: !!user,
    }
  );

  // Create rubric from template
  const createRubric = async (
    focusId: string, 
    templateType: string, 
    customizations?: Partial<Rubric>
  ): Promise<Rubric | null> => {
    if (!user) return null;

    setIsCreating(true);
    try {
      // Find the template
      const template = templates?.find(t => t.template_type === templateType);
      if (!template) {
        showToast('Template not found', 'error');
        return null;
      }

      // Create rubric data
      const rubricData = {
        user_id: user.id,
        focus_id: focusId,
        template_type: templateType,
        name: customizations?.name || template.name,
        description: customizations?.description || template.description,
        categories: customizations?.categories || template.categories,
        is_active: true,
      };

      const { data, error } = await supabase
        .from('internship_rubrics')
        .insert(rubricData)
        .select()
        .single();

      if (error) throw error;

      // Invalidate caches
      await invalidateCache(`${CACHE_KEYS.INTERNSHIP_RUBRIC}_${focusId}`);
      
      showToast('Rubric created successfully', 'success');
      return data;
    } catch (error) {
      console.error('Error creating rubric:', error);
      showToast('Failed to create rubric', 'error');
      return null;
    } finally {
      setIsCreating(false);
    }
  };

  // Update rubric
  const updateRubric = async (
    rubricId: string, 
    updates: Partial<Rubric>
  ): Promise<boolean> => {
    if (!user) return false;

    setIsUpdating(true);
    try {
      const { error } = await supabase
        .from('internship_rubrics')
        .update(updates)
        .eq('id', rubricId)
        .eq('user_id', user.id);

      if (error) throw error;

      // Invalidate caches
      if (focusId) {
        await invalidateCache(`${CACHE_KEYS.INTERNSHIP_RUBRIC}_${focusId}`);
      }
      
      showToast('Rubric updated successfully', 'success');
      return true;
    } catch (error) {
      console.error('Error updating rubric:', error);
      showToast('Failed to update rubric', 'error');
      return false;
    } finally {
      setIsUpdating(false);
    }
  };

  // Deactivate rubric
  const deactivateRubric = async (rubricId: string): Promise<boolean> => {
    return updateRubric(rubricId, { is_active: false });
  };

  // Get rubric for a specific focus
  const getRubricForFocus = async (focusId: string): Promise<Rubric | null> => {
    if (!user) return null;

    const { data, error } = await supabase
      .from('internship_rubrics')
      .select('*')
      .eq('focus_id', focusId)
      .eq('is_active', true)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error fetching rubric:', error);
      return null;
    }

    return data;
  };

  // Update category
  const updateCategory = async (
    categoryId: string,
    updates: Partial<RubricCategory>
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('rubric_categories')
        .update({
          name: updates.name,
          description: updates.description,
          weight: updates.weight,
          display_order: updates.display_order
        })
        .eq('id', categoryId);

      if (error) throw error;

      await refetchRubric();
      showToast('Category updated successfully', 'success');
      return true;
    } catch (error) {
      console.error('Failed to update category:', error);
      showToast('Failed to update category', 'error');
      return false;
    }
  };

  // Delete category
  const deleteCategory = async (categoryId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      // Check for evidence links to criteria in this category
      const { data: criteria } = await supabase
        .from('rubric_criteria')
        .select('id')
        .eq('category_id', categoryId);

      if (criteria && criteria.length > 0) {
        const criteriaIds = criteria.map(c => c.id);
        const { data: links } = await supabase
          .from('content_links')
          .select('id')
          .eq('target_type', 'rubric_criterion')
          .in('target_id', criteriaIds);

        if (links && links.length > 0) {
          const confirmed = window.confirm(
            `This category has ${links.length} evidence links. Deleting it will remove these links. Continue?`
          );
          if (!confirmed) return false;
        }
      }

      const { error } = await supabase
        .from('rubric_categories')
        .delete()
        .eq('id', categoryId);

      if (error) throw error;

      await refetchRubric();
      showToast('Category deleted successfully', 'success');
      return true;
    } catch (error) {
      console.error('Failed to delete category:', error);
      showToast('Failed to delete category', 'error');
      return false;
    }
  };

  // Add category
  const addCategory = async (
    rubricId: string,
    category: Omit<RubricCategory, 'id' | 'rubric_id' | 'created_at'>
  ): Promise<RubricCategory | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('rubric_categories')
        .insert({
          rubric_id: rubricId,
          name: category.name,
          description: category.description,
          weight: category.weight,
          display_order: category.display_order
        })
        .select()
        .single();

      if (error) throw error;

      await refetchRubric();
      showToast('Category added successfully', 'success');
      return data;
    } catch (error) {
      console.error('Failed to add category:', error);
      showToast('Failed to add category', 'error');
      return null;
    }
  };

  // Update criterion
  const updateCriterion = async (
    criterionId: string,
    updates: Partial<RubricCriterion>
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      const { error } = await supabase
        .from('rubric_criteria')
        .update({
          text: updates.text,
          description: updates.description,
          display_order: updates.display_order
        })
        .eq('id', criterionId);

      if (error) throw error;

      await refetchRubric();
      showToast('Criterion updated successfully', 'success');
      return true;
    } catch (error) {
      console.error('Failed to update criterion:', error);
      showToast('Failed to update criterion', 'error');
      return false;
    }
  };

  // Delete criterion
  const deleteCriterion = async (criterionId: string): Promise<boolean> => {
    if (!user) return false;

    try {
      // Check for evidence links
      const { data: links } = await supabase
        .from('content_links')
        .select('id')
        .eq('target_type', 'rubric_criterion')
        .eq('target_id', criterionId);

      if (links && links.length > 0) {
        const confirmed = window.confirm(
          `This criterion has ${links.length} evidence links. Deleting it will remove these links. Continue?`
        );
        if (!confirmed) return false;
      }

      const { error } = await supabase
        .from('rubric_criteria')
        .delete()
        .eq('id', criterionId);

      if (error) throw error;

      await refetchRubric();
      showToast('Criterion deleted successfully', 'success');
      return true;
    } catch (error) {
      console.error('Failed to delete criterion:', error);
      showToast('Failed to delete criterion', 'error');
      return false;
    }
  };

  // Add criterion
  const addCriterion = async (
    categoryId: string,
    criterion: Omit<RubricCriterion, 'id' | 'category_id' | 'created_at'>
  ): Promise<RubricCriterion | null> => {
    if (!user) return null;

    try {
      const { data, error } = await supabase
        .from('rubric_criteria')
        .insert({
          category_id: categoryId,
          text: criterion.text,
          description: criterion.description,
          display_order: criterion.display_order
        })
        .select()
        .single();

      if (error) throw error;

      await refetchRubric();
      showToast('Criterion added successfully', 'success');
      return data;
    } catch (error) {
      console.error('Failed to add criterion:', error);
      showToast('Failed to add criterion', 'error');
      return null;
    }
  };

  // Reorder categories
  const reorderCategories = async (categoryIds: string[]): Promise<boolean> => {
    if (!user || !activeRubric) return false;

    try {
      // First, set all categories to temporary high values to avoid conflicts
      const tempOffset = 1000;
      
      // Step 1: Move all to temporary positions
      for (let i = 0; i < categoryIds.length; i++) {
        const { error } = await supabase
          .from('rubric_categories')
          .update({ display_order: tempOffset + i })
          .eq('id', categoryIds[i])
          .eq('rubric_id', activeRubric.id);

        if (error) {
          console.error(`Failed to move category ${categoryIds[i]} to temp position:`, error);
          throw error;
        }
      }

      // Step 2: Move to final positions
      for (let i = 0; i < categoryIds.length; i++) {
        const { error } = await supabase
          .from('rubric_categories')
          .update({ display_order: i })
          .eq('id', categoryIds[i])
          .eq('rubric_id', activeRubric.id);

        if (error) {
          console.error(`Failed to update category ${categoryIds[i]} to final position:`, error);
          throw error;
        }
      }

      await refetchRubric();
      showToast('Categories reordered successfully', 'success');
      return true;
    } catch (error) {
      console.error('Failed to reorder categories:', error);
      showToast('Failed to reorder categories', 'error');
      return false;
    }
  };

  // Reorder criteria
  const reorderCriteria = async (
    categoryId: string,
    criterionIds: string[]
  ): Promise<boolean> => {
    if (!user) return false;

    try {
      // First, set all criteria to temporary high values to avoid conflicts
      const tempOffset = 1000;
      
      // Step 1: Move all to temporary positions
      for (let i = 0; i < criterionIds.length; i++) {
        const { error } = await supabase
          .from('rubric_criteria')
          .update({ display_order: tempOffset + i })
          .eq('id', criterionIds[i])
          .eq('category_id', categoryId);

        if (error) {
          console.error(`Failed to move criterion ${criterionIds[i]} to temp position:`, error);
          throw error;
        }
      }

      // Step 2: Move to final positions
      for (let i = 0; i < criterionIds.length; i++) {
        const { error } = await supabase
          .from('rubric_criteria')
          .update({ display_order: i })
          .eq('id', criterionIds[i])
          .eq('category_id', categoryId);

        if (error) {
          console.error(`Failed to update criterion ${criterionIds[i]} to final position:`, error);
          throw error;
        }
      }

      await refetchRubric();
      showToast('Criteria reordered successfully', 'success');
      return true;
    } catch (error) {
      console.error('Failed to reorder criteria:', error);
      showToast('Failed to reorder criteria', 'error');
      return false;
    }
  };

  return {
    activeRubric,
    templates,
    rubricLoading,
    templatesLoading,
    isCreating,
    isUpdating,
    createRubric,
    updateRubric,
    deactivateRubric,
    getRubricForFocus,
    refetchRubric,
    // New functions
    updateCategory,
    deleteCategory,
    addCategory,
    updateCriterion,
    deleteCriterion,
    addCriterion,
    reorderCategories,
    reorderCriteria,
  };
}